import { useEffect } from "react";
import { ArrowLeft, Maximize, LayoutPanelTop } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import VideoPosterFrame from "@/components/VideoPosterFrame";
import ProgressiveImg from "@/components/ProgressiveImg";

const typeLabel = (t) => {
  switch (t) {
    case 2: return "FLOORPLAN";
    case 3: return "EPC";
    case 4: return "BROCHURE";
    case 5: return "VIDEO";
    case 6: return "TOUR";
    default: return null;
  }
};

/**
 * Full-page "Media gallery" grid — mirrors the mobile Rightmove-style
 * gallery: a sticky dark header with a back arrow, every photo shown at
 * once in an alternating 2-up / full-width grid, and a floating
 * "Floorplan" pill that jumps straight to the floorplan image. Tapping
 * any tile hands off to the parent's full-screen single-image lightbox
 * (`onSelectImage`) — this component only renders the grid itself.
 */
export default function MediaGalleryGrid({ open, onClose, items = [], onSelectImage, suspendEscape = false }) {
  // Escape closes the grid, and the body scroll is locked while it's open —
  // matches the existing PropertyGalleryModal lightbox's own key handling.
  // `suspendEscape` is true while the lightbox is open on top of this grid,
  // so Escape closes only the lightbox (its own listener), not both layers.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !suspendEscape) onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, suspendEscape]);

  if (!open) return null;

  const floorplanIndex = items.findIndex((it) => it.media_type === 2);

  return (
    <div className="fixed inset-0 z-[180] bg-white overflow-y-auto" data-testid="media-gallery-grid">
      <div className="sticky top-0 z-10 bg-[#0b1638] text-white flex items-center gap-4 px-4 py-4 shadow-md">
        <button
          type="button"
          onClick={onClose}
          aria-label="Back"
          className="h-8 w-8 flex items-center justify-center -ml-1"
          data-testid="media-gallery-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-base font-medium" style={{ color: "#ffffff" }}>Media gallery</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 pb-28 max-w-4xl mx-auto">
        {items.map((it, i) => {
          const isFullWidth = i % 3 === 2;
          const isVideo = it.media_type === 5;
          const isTour = it.media_type === 6;
          const label = typeLabel(it.media_type);
          return (
            <button
              type="button"
              key={`${it.url}-${i}`}
              onClick={() => onSelectImage(i)}
              className={`relative overflow-hidden bg-black ${isFullWidth ? "col-span-2 aspect-[16/10]" : "aspect-[4/3]"}`}
              data-testid={`media-gallery-grid-item-${i}`}
              aria-label={`Open image ${i + 1}`}
            >
              {isTour ? (
                <div className="w-full h-full bg-gradient-to-br from-primary/80 to-primary flex flex-col items-center justify-center text-white">
                  <Maximize size={28} className="mb-1 opacity-90" />
                  <span className="text-xs font-medium tracking-wide">3D TOUR</span>
                </div>
              ) : isVideo ? (
                <div className="w-full h-full relative">
                  <VideoPosterFrame src={resolveImageUrl(it.url)} />
                </div>
              ) : (
                <ProgressiveImg
                  src={resolveImageUrl(it.url)}
                  alt={it.caption || `Image ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full"
                />
              )}
              {label && (
                <span className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded uppercase">
                  {label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {floorplanIndex > -1 && (
        <button
          type="button"
          onClick={() => onSelectImage(floorplanIndex)}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0b1638] text-white rounded-full pl-4 pr-5 py-3 shadow-xl flex items-center gap-2 hover:bg-[#132152] transition-colors"
          data-testid="media-gallery-floorplan-btn"
        >
          <LayoutPanelTop className="h-4 w-4" />
          <span className="text-sm font-medium">Floorplan</span>
        </button>
      )}
    </div>
  );
}
